import type { EntityTypeDefinition } from '@/admin/hooks/use-entities'

export interface EntityTreeRow {
  entity: EntityTypeDefinition
  depth: number
}

export function entityParentKey(entity: EntityTypeDefinition) {
  const parentKey = entity.metadata?.parent_entity_key
  return typeof parentKey === 'string' && parentKey.trim() ? parentKey.trim() : null
}

export function buildEntityTreeRows(
  entities: EntityTypeDefinition[],
  compareEntities: (a: EntityTypeDefinition, b: EntityTypeDefinition) => number,
): EntityTreeRow[] {
  const byKey = new Map(entities.map((entity) => [entity.key, entity]))
  const childrenByParent = new Map<string, EntityTypeDefinition[]>()
  const roots: EntityTypeDefinition[] = []

  for (const entity of entities) {
    const parentKey = entityParentKey(entity)
    if (!parentKey || !byKey.has(parentKey)) {
      roots.push(entity)
      continue
    }
    const siblings = childrenByParent.get(parentKey) ?? []
    siblings.push(entity)
    childrenByParent.set(parentKey, siblings)
  }

  roots.sort(compareEntities)
  for (const siblings of childrenByParent.values()) {
    siblings.sort(compareEntities)
  }

  const rows: EntityTreeRow[] = []
  const visited = new Set<string>()

  function visit(entity: EntityTypeDefinition, depth: number, path: Set<string>) {
    if (path.has(entity.key)) return
    if (visited.has(entity.key)) return
    rows.push({ entity, depth })
    visited.add(entity.key)

    const children = childrenByParent.get(entity.key) ?? []
    const nextPath = new Set(path)
    nextPath.add(entity.key)
    for (const child of children) {
      visit(child, depth + 1, nextPath)
    }
  }

  for (const root of roots) {
    visit(root, 0, new Set())
  }

  const unvisited = entities
    .filter((entity) => !visited.has(entity.key))
    .sort(compareEntities)
  for (const entity of unvisited) {
    visit(entity, 0, new Set())
  }

  return rows
}
